import { useEffect, useState } from "react";

import { ObjectDataDefinition } from "../../rule-engine-core/Common/Data/Definitions/ObjectDataDefinition";
import { DataDefinition } from "../../rule-engine-core/Common/Data/DataDefinition";
import { EditModeObjectValueEditor } from "./Object";
import { UIStandardLayer } from "../BuildlingBlocks/UIStandardLayer";
import { Box } from "@mui/material";
import { UIToggleButtons } from "../BuildlingBlocks/UIToggleButtons";
import { OpenAIChatLink } from "../OpenAI/OpenAIChatLink";
import JSONInput from "react-json-editor-ajrm";
import { locale } from "../../rule-engine-core/Common/JsonInputLocale";
import { DataUtilities } from "../../rule-engine-core/Common/DataUtilities";
import type { AppGlueAny } from "../../rule-engine-core/Common/AppGlueAny";

export const DefinitionBasedDataEditor = ({
    open,
    data,
    title,
    updateCallback,
    value,
    doneButtonTitle,
    onClose,
}: {
    open: boolean;
    data: ObjectDataDefinition | DataDefinition;
    updateCallback: (newValue: AppGlueAny) => void;
    value: AppGlueAny;
    doneButtonTitle?: string;
    title: string;
    onClose: () => void;
}) => {
    const [objValues, setValues] = useState<AppGlueAny>(value);
    const [editorType, setEditorType] = useState<"form" | "text value">("form");

    useEffect(() => {
        setValues(value);
    }, [value]);

    let field: ObjectDataDefinition = new ObjectDataDefinition();

    if (data instanceof ObjectDataDefinition) {
        field = data;
    } else {
        field.fields = data.fields;
    }

    const onValueUpdate = (newValue: AppGlueAny) => {
        setValues(newValue);
    };

    function getSchema(): string {
        let dd = new DataDefinition();
        dd.fields = field.fields;
        return dd.toJSONSchema();
    }

    const onClear = () => {
        if (field.list) {
            setValues([]);
        } else {
            setValues({});
        }
    };

    const onDone = () => {
        updateCallback(objValues);
    };

    return (
        <UIStandardLayer
            title={title}
            primaryActionText={doneButtonTitle ?? "Done"}
            onPrimaryAction={onDone}
            secondaryActionText={"Clear"}
            onSecondaryAction={onClear}
            open={open}
            onClose={onClose}
            renderUI={() => (
                <Box
                    display={"flex"}
                    flexDirection={"column"}
                    height={"70vh"}
                >
                    <Box
                        display={"flex"}
                        flexDirection={"row"}
                        alignItems={"center"}
                        justifyContent={"space-between"}
                    >
                        <Box width={"160px"}></Box>
                        <UIToggleButtons
                            options={["form", "text value"]}
                            value={editorType}
                            onChange={(v) => {
                                if (v === "form") setEditorType("form");
                                else setEditorType("text value");
                            }}
                        />
                        <OpenAIChatLink
                            linkName={"Generate Test Data"}
                            title={"Test Data Generation"}
                            initialPrompt={"generate JSON for JSON Schema:  " + getSchema()}
                            onSelectAnswer={(a) => {
                                let obj = DataUtilities.parseJSON(a);
                                setValues(obj);
                            }}
                            needsClean={(t: string) => {
                                try {
                                    JSON.parse(t);
                                } catch {
                                    return true;
                                }
                                return false;
                            }}
                        />
                    </Box>

                    <Box
                        width={"800px"}
                        sx={{ overflowY: "auto" }}
                        padding={"10px"}
                        height={"100%"}
                    >
                        {editorType === "form" && (
                            <EditModeObjectValueEditor
                                forData={field!}
                                value={objValues}
                                editMode="inline"
                                onUpdate={onValueUpdate}
                            />
                        )}
                        {editorType !== "form" && (
                            <JSONInput
                                height={"100%"}
                                placeholder={objValues}
                                locale={locale}
                                theme={"light_mitsuketa_tribute"}
                                style={{
                                    outerBox: {
                                        width: "100%",
                                    },
                                    container: {
                                        width: "100%",
                                    },
                                }}
                                onChange={(v: AppGlueAny) => {
                                    setValues(Reflect.get(v, "jsObject"));
                                }}
                            />
                        )}
                    </Box>
                </Box>
            )}
        ></UIStandardLayer>
    );
};
